/**
 * EventBanner - Auto-scrolling banner for featured events
 * 
 * Shows events that have show_on_homepage = true
 * Tammy can toggle this in the admin portal
 * Clicking navigates to the event's landing page
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HiChevronLeft, HiChevronRight, HiCalendar, HiClock } from 'react-icons/hi';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';

interface EventCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  image_url: string | null;
  color: string;
  is_active: boolean;
  has_landing_page: boolean;
  landing_page_content: {
    hero?: {
      title?: string;
      subtitle?: string;
      description?: string;
      image_url?: string;
    };
    event_details?: {
      date?: string;
      time?: string;
      price?: string;
      price_text?: string;
    };
  } | null;
  show_on_homepage: boolean;
  homepage_priority: number;
  homepage_tagline: string | null;
}

interface EventBannerProps {
  className?: string;
  autoScrollInterval?: number; // milliseconds, default 5000
}

export default function EventBanner({ 
  className = '', 
  autoScrollInterval = 5000 
}: EventBannerProps) {
  const { getImage } = useSiteImageOverrides();
  const [events, setEvents] = useState<EventCategory[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Fetch featured events
  useEffect(() => {
    async function fetchEvents() {
      try {
        const response = await fetch('/api/event-categories?active=true&homepage=true');
        const data = await response.json();
        
        // Filter and sort by priority
        const featuredEvents = (data.categories || [])
          .filter((e: EventCategory) => e.show_on_homepage && e.is_active)
          .sort((a: EventCategory, b: EventCategory) => a.homepage_priority - b.homepage_priority);
        
        setEvents(featuredEvents);
      } catch (error) {
        console.error('Failed to fetch featured events:', error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchEvents();
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (events.length <= 1 || isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % events.length);
    }, autoScrollInterval);

    return () => clearInterval(timer);
  }, [events.length, autoScrollInterval, isPaused]);

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + events.length) % events.length);
  }, [events.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % events.length);
  }, [events.length]);

  // Don't render if no events or still loading
  if (isLoading || events.length === 0) {
    return null;
  }

  const currentEvent = events[currentIndex];
  const heroContent = currentEvent.landing_page_content?.hero;
  const eventDetails = currentEvent.landing_page_content?.event_details;
  
  // Get the best image: landing page hero > category image > fallback (fallback is overridable via Site Images)
  // Missing placeholder-event.webp 404s as HTML; use the live logo instead.
  const imageUrl = heroContent?.image_url || currentEvent.image_url || getImage('placeholder_event', '/assets/images/branding/logo2.webp');
  
  // Get display text
  const title = heroContent?.title || currentEvent.name;
  const tagline = currentEvent.homepage_tagline || heroContent?.subtitle || currentEvent.description;

  return (
    <section 
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      aria-label="Featured Events"
    >
      {/* Background with gradient overlay */}
      <div className="absolute inset-0">
        <img
          src={imageUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <div className="max-w-2xl">
          {/* Tagline badge */}
          {tagline && (
            <span 
              className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-4
                         bg-white/20 backdrop-blur-sm text-white border border-white/30"
            >
              ✨ {tagline}
            </span>
          )}

          {/* Title */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {title}
          </h2>

          {/* Event details */}
          {eventDetails && (
            <div className="flex flex-wrap gap-4 mb-6 text-white/90">
              {eventDetails.date && (
                <span className="flex items-center gap-2">
                  <HiCalendar className="w-5 h-5" />
                  {eventDetails.date}
                </span>
              )}
              {eventDetails.time && (
                <span className="flex items-center gap-2">
                  <HiClock className="w-5 h-5" />
                  {eventDetails.time}
                </span>
              )}
              {eventDetails.price && (
                <span className="font-semibold text-pink-300">
                  ${eventDetails.price} {eventDetails.price_text}
                </span>
              )}
            </div>
          )}

          {/* CTA Button */}
          <Link
            to={currentEvent.has_landing_page ? `/events/${currentEvent.slug}` : '/contact'}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full
                       bg-pink-500 hover:bg-pink-600 text-white font-semibold
                       transition-all duration-300 transform hover:scale-105
                       shadow-lg hover:shadow-pink-500/30"
          >
            Learn More & Reserve
            <HiChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Navigation arrows (only show if multiple events) */}
      {events.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20
                       w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm
                       flex items-center justify-center text-white
                       hover:bg-white/30 transition-colors"
            aria-label="Previous event"
          >
            <HiChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20
                       w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm
                       flex items-center justify-center text-white
                       hover:bg-white/30 transition-colors"
            aria-label="Next event"
          >
            <HiChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {events.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {events.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to event ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
